/**
 * Bundle Pricing Integration for Shopify
 * Works with existing enhanced-variant-picker.js
 */

class BundlePricingManager {
  constructor() {
    this.debugMode = false;
    this.currentBundle = {
      quantity: 1,
      basePrice: 0,
      discountedPrice: 0,
      items: []
    };
      
    // Default unit types
    this.unitType = {
      singular: 'ITEM',
      plural: 'ITEMS',
      lowercase: {
        singular: 'item',
        plural: 'items'
      }
    };
      
    // Bundle pricing tiers (will be overridden by metafield data if available)
    this.pricingTiers = [
      { quantity: 1, discount: 0, label: '1 ITEM' },
      { quantity: 4, discount: 0.1, label: '4 ITEMS', badge: '10% OFF' },
      { quantity: 6, discount: 0.2, label: '6 ITEMS', badge: '20% OFF' },
      { quantity: 8, discount: 0.3, label: '8 ITEMS', badge: '30% OFF' },
      { quantity: 10, discount: 0.4, label: '10 ITEMS', badge: 'BEST - 40% OFF', isBest: true }
    ];
      
    // Default selection (will be overridden by metafield data if available)
    this.defaultSelection = null;

    // Safe default in case createBundleInterface() bails before populating this
    this.bundleOptionElements = [];

    // Pack mode ("Choose your pack" picker). Set from BundlePricingData.packMode.
    this.packMode = '';
    this.isPack = false;
    this.freeShippingThresholdCents = 0;
    this.moneyFormat = '${{amount}}';

    this.init();
  }
  
  debug(...args) {
    if (this.debugMode) {
      console.log('[BundlePricing]', ...args);
    }
  }
  
  init() {
    // Wait for DOM and enhanced variant picker to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }
  
  setup() {
    this.debug('Setting up bundle pricing...');
      
    // Don't run in cart drawer context
    if (document.querySelector('cart-drawer.active')) {
      this.debug('Bundle pricing disabled in cart drawer');
      return;
    }
      
    // Check if bundle pricing should be enabled for this product
    if (!this.shouldEnableBundlePricing()) {
      this.debug('Bundle pricing disabled for this product - stopping setup');
      return;
    }
      
    // Load pricing tiers from metafield data if available
    this.loadPricingTiers();
      
    // Find the enhanced variant picker
    this.variantPicker = document.querySelector('enhanced-variant-picker');
    this.productForm = document.querySelector('product-form');
      
    if (!this.variantPicker) {
      this.debug('Enhanced variant picker not found, waiting...');
      setTimeout(() => this.setup(), 500);
      return;
    }
  
    // Cache DOM nodes/parsed data reused on every interaction. Re-querying the
    // document and re-parsing ProductJson on each variant:change/bundle-option
    // click was a measured INP cost (~44ms/interaction) on the Helios PDP.
    this.variantIdInputEl = this.productForm
      ? this.productForm.querySelector('input[name="id"]')
      : document.querySelector('input[name="id"]');
    this.addToCartBtn = document.querySelector('.product-form__cart-submit') ||
                         document.querySelector('.product-form button[name="add"]') ||
                         document.querySelector('product-form button[name="add"]');
    this.optionContainerElements = Array.from(document.querySelectorAll('.option-container'));

    const productJsonElement = document.querySelector('[id*="ProductJson"]');
    try {
      this.variantsData = productJsonElement ? JSON.parse(productJsonElement.textContent) : [];
    } catch {
      this.variantsData = [];
    }

    // Get base price from the product
    this.getBasePrice();

    // Pack mode renders into server-rendered slots and has its own listeners.
    if (this.packMode && document.querySelector('[data-wr-pack]')) {
      this.setupPack();
      return;
    }

    // Create bundle interface
    this.createBundleInterface();

    // Listen for variant changes from enhanced picker
    this.setupVariantListeners();
      
    // Initialize with default selection
    const defaultQty = this.getDefaultSelection();
    this.updateBundle(defaultQty);
      
    // Sync with current variant from enhanced picker
    this.syncWithCurrentVariant();
      
    // Initialize add to cart button
    this.updateAddToCartButton();
  }
    
  shouldEnableBundlePricing() {
    // Check if the bundle pricing data script exists (only rendered when product has tag)
    const bundleData = document.getElementById('BundlePricingData-' + this.getSectionId());
      
    if (!bundleData) {
      this.debug('No bundle pricing data found - product not tagged for bundles');
      return false;
    }
      
    return true;
  }
  
  getSectionId() {
    // Get section ID from product-info element
    const productInfo = document.querySelector('product-info');
    if (productInfo && productInfo.dataset.section) {
      return productInfo.dataset.section;
    }
      
    // Fallback: try to find it in the URL or DOM
    const bodyId = document.body.id;
    if (bodyId && bodyId.includes('product')) {
      return bodyId.split('--')[1] || 'main';
    }
      
    return 'main';
  }
  
  loadPricingTiers() {
    // Try to load custom pricing tiers from metafield data
    const bundleDataElement = document.getElementById('BundlePricingData-' + this.getSectionId());
      
    if (bundleDataElement) {
      try {
        const bundleData = JSON.parse(bundleDataElement.textContent);
          
        // Load pricing tiers
        if (bundleData.pricingTiers && bundleData.pricingTiers.length > 0) {
          this.pricingTiers = bundleData.pricingTiers;
          this.debug('Loaded custom pricing tiers:', this.pricingTiers);
        }
          
        // Load default selection if provided
        if (bundleData.defaultSelection !== undefined && bundleData.defaultSelection !== null) {
          this.defaultSelection = bundleData.defaultSelection;
          this.debug('Loaded custom default selection:', this.defaultSelection);
        }
          
        // Load unit types if provided
        if (bundleData.unitType) {
          this.unitType = bundleData.unitType;
          this.debug('Loaded custom unit type:', this.unitType);
            
          // Update tier labels with custom unit
          this.updateTierLabels();
        }

        this.packMode = bundleData.packMode || '';
        this.freeShippingThresholdCents = bundleData.freeShippingThreshold || 0;
        if (bundleData.moneyFormat) this.moneyFormat = bundleData.moneyFormat;
        if (this.packMode === 'preview') this.loadPackPreviewOverride();
      } catch (e) {
        this.debug('Error parsing bundle data, using defaults:', e);
      }
    }
      
    // Always log the final pricing tiers being used
    this.debug('Final pricing tiers:', this.pricingTiers);
    this.debug('Default selection:', this.defaultSelection);
  }
    
  updateTierLabels() {
    // Update the pricing tier labels with the custom unit type
    // Only auto-generate labels if they're not already provided in the JSON
    this.pricingTiers = this.pricingTiers.map(tier => {
      // If tier already has a label from JSON, keep it
      if (tier.label) {
        return tier;
      }
      // Otherwise, generate one from quantity and unit type
      const unit = tier.quantity === 1 ? this.unitType.singular : this.unitType.plural;
      return {
        ...tier,
        label: `${tier.quantity} ${unit}`
      };
    });
  }
    
  getDefaultSelection() {
    // Check if there's a custom default selection from metafield
    if (this.defaultSelection !== null && this.defaultSelection !== undefined) {
      // Verify the selection exists in pricing tiers
      const tierExists = this.pricingTiers.some(tier => tier.quantity === this.defaultSelection);
      if (tierExists) {
        this.debug('Using custom default selection from metafield:', this.defaultSelection);
        return this.defaultSelection;
      } else {
        this.debug('Custom default selection not found in tiers, falling back to default logic');
      }
    }
      
    // Fallback to default logic: second tier if multiple tiers exist, otherwise 1
    const fallbackDefault = this.pricingTiers.length > 1 ? this.pricingTiers[1].quantity : 1;
    this.debug('Using fallback default selection:', fallbackDefault);
    return fallbackDefault;
  }
  
  getBasePrice() {
    // Prefer the authoritative cents-formatted basePrice rendered into BundlePricingData.
    // The previous fallback (`[data-price]`) collided with unrelated cross-sell sections
    // that put cents values on checkboxes, producing 100x-inflated totals (e.g. $3591).
    const bundleDataElement = document.getElementById('BundlePricingData-' + this.getSectionId());
    if (bundleDataElement) {
      try {
        const bundleData = JSON.parse(bundleDataElement.textContent);
        if (typeof bundleData.basePrice === 'number') {
          this.currentBundle.basePrice = bundleData.basePrice / 100;
          this.debug('Base price set from BundlePricingData:', this.currentBundle.basePrice);
          return;
        }
      } catch (e) {
        this.debug('Error parsing BundlePricingData for basePrice:', e);
      }
    }

    // Fallback: read formatted money from the price block (dollars, not cents).
    const priceElement = document.querySelector('.price__regular .money');
    if (priceElement) {
      this.currentBundle.basePrice = parseFloat(priceElement.textContent.replace(/[^0-9.]/g, ''));
      this.debug('Base price set from .price__regular .money:', this.currentBundle.basePrice);
      return;
    }

    this.currentBundle.basePrice = 24.99;
    this.debug('Base price fell back to default:', this.currentBundle.basePrice);
  }
  
  createBundleInterface() {
    // Find where to insert the bundle interface
    const buyButtonsBlock = document.querySelector('[data-block-type="buy_buttons"]') ||
                             document.querySelector('.product-form__buttons') ||
                             document.querySelector('product-form');
      
    if (!buyButtonsBlock) {
      this.debug('Buy buttons block not found');
      return;
    }
  
    // Create bundle container
    const bundleContainer = document.createElement('div');
    bundleContainer.className = 'bundle-pricing-container';
    bundleContainer.innerHTML = this.generateBundleHTML();
      
    // Insert before buy buttons
    buyButtonsBlock.parentNode.insertBefore(bundleContainer, buyButtonsBlock);

    // Cache the option elements now that they exist in the DOM
    this.bundleOptionElements = Array.from(document.querySelectorAll('.bundle-option'));

    // Add event listeners
    this.setupBundleEventListeners();
      
    // Intercept form submission to ensure correct variant
    this.interceptFormSubmission();
      
    // Add CSS
    this.addBundleStyles();
  }
    
  interceptFormSubmission() {
    // Find all forms that submit to cart
    const forms = document.querySelectorAll('form[action*="/cart/add"]');
    this.debug(`Found ${forms.length} cart forms to intercept`);
      
    forms.forEach(form => {
      // Add a capture phase listener to intercept before other handlers
      form.addEventListener('submit', (e) => {
        this.debug('=== FORM SUBMISSION INTERCEPTED ===');
          
        // If bundle quantity is 1, ensure the form has the correct variant
        if (this.currentBundle.quantity === 1) {
          const formVariantInput = form.querySelector('input[name="id"]');
          const currentFormValue = formVariantInput?.value;
            
          // Get the current variant from the page
          const correctVariantId = this.variantIdInputEl?.value;
            
          this.debug('Form submission check:', {
            bundleQuantity: this.currentBundle.quantity,
            formVariantId: currentFormValue,
            correctVariantId: correctVariantId,
            bundleItemVariantId: this.currentBundle.items[0]?.variantId
          });
            
          // Update the form's variant ID if needed
          if (correctVariantId && formVariantInput && formVariantInput.value !== correctVariantId) {
            this.debug(`Updating form variant from ${formVariantInput.value} to ${correctVariantId}`);
            formVariantInput.value = correctVariantId;
          }
        }
      }, true); // true = capture phase, runs before other handlers
    });
  }
  
  generateBundleHTML() {
    const defaultQuantity = this.getDefaultSelection();
    const defaultTier = this.pricingTiers.find(t => t.quantity === defaultQuantity);
    const defaultLabel = defaultTier ? defaultTier.label : `${defaultQuantity} ITEM${defaultQuantity > 1 ? 'S' : ''}`;

    return `
        <div class="bundle-section">
          <h3 class="bundle-title">BUNDLE & SAVE! <span class="bundle-subtitle">${defaultLabel}</span></h3>
          
          <div class="bundle-options" id="bundleOptions">
            ${this.pricingTiers.map(tier => `
              <div class="bundle-option ${tier.quantity === 1 ? 'single-item' : ''}" 
                   data-quantity="${tier.quantity}" 
                   data-discount="${tier.discount}"
                   ${tier.price !== undefined ? `data-price="${tier.price}"` : ''}
                   ${tier.quantity === defaultQuantity ? 'data-selected="true"' : ''}>
                ${tier.badge ? `<div class="bundle-discount ${tier.isBest ? 'best-offer' : ''}">${tier.badge}</div>` : ''}
                <div class="bundle-quantity">${tier.label}</div>
                <div class="bundle-price">$${this.calculatePrice(tier.discount, tier).toFixed(2)}/${this.unitType.lowercase.singular}</div>
                ${tier.subtext ? `<div class="bundle-subtext">${tier.subtext}</div>` : ''}
              </div>
            `).join('')}
          </div>
  
          <div class="bundle-customization" id="bundleCustomization" style="display: none;">
            <div class="bundle-customization-header" data-bundle-customization-header>
              <span class="customization-label">Customize Your Bundle:</span>
              <button type="button" class="collapse-btn" aria-expanded="true" aria-controls="bundleItems">
                <span class="collapse-text">Collapse</span><span class="visually-hidden"> bundle customization</span>
              </button>
            </div>
            <div class="bundle-items" id="bundleItems"></div>
          </div>
        </div>
      `;
  }
  
  calculatePrice(discount, tier = null) {
    // If tier has an absolute price, use that instead of calculating from discount
    if (tier && tier.price !== undefined && tier.price !== null) {
      this.debug(`Using absolute price: $${tier.price}`);
      return tier.price;
    }
      
    const discountedPrice = this.currentBundle.basePrice * (1 - discount);
    this.debug(`Price calculation: base=$${this.currentBundle.basePrice}, discount=${discount * 100}%, result=$${discountedPrice.toFixed(2)}`);
    return discountedPrice;
  }
  
  setupBundleEventListeners() {
    // Bundle option selection
    this.bundleOptionElements.forEach(option => {
      option.addEventListener('click', (e) => {
        const quantity = parseInt(e.currentTarget.dataset.quantity);
        const discount = parseFloat(e.currentTarget.dataset.discount);
        this.updateBundle(quantity, discount);
      });
    });

    // The whole "Customize Your Bundle:" row is the toggle's click target, so
    // clicks on the label are no longer dead clicks. The .collapse-btn stays
    // the single focusable control (aria-expanded/aria-controls live on it);
    // its clicks, including Enter/Space activation, bubble up to this one
    // listener, so every activation toggles exactly once.
    const customizationHeader = document.querySelector('[data-bundle-customization-header]');
    if (customizationHeader) {
      customizationHeader.addEventListener('click', () => {
        this.toggleCustomization();
      });
    }
  }
  
  setupVariantListeners() {
    // Only listen to the DOM event to avoid duplicates
    document.addEventListener('variant:change', (event) => {
      this.debug('Variant changed via DOM event:', event.detail);

      // handleVariantChange is idempotent, so the 50ms debounce was pure
      // added latency on every interaction (INP fix) — call it directly.
      this.handleVariantChange(event.detail.variant);

      // Consolidate with bundle-price-display.js's update into this single
      // path instead of a second, un-debounced variant:change listener.
      if (window.bundlePriceDisplay) {
        window.bundlePriceDisplay.updateBundlePrices(event.detail.variant);
      }
    });
  }
  
  handleVariantChange(variant) {
    if (!variant) {
      this.debug('handleVariantChange called with no variant');
      return;
    }
      
    this.debug('=== handleVariantChange START ===');
    this.debug('New variant:', variant);
    this.debug('Current bundle quantity:', this.currentBundle.quantity);
    if (this.debugMode) this.debug('Current items before variant change:', JSON.parse(JSON.stringify(this.currentBundle.items)));
      
    // Update base price from new variant
    this.currentBundle.basePrice = variant.price / 100; // Convert from cents
      
    // Update all bundle items to use the new variant
    const currentOptions = this.getCurrentVariantOptions();
    this.debug('Updating items for quantity:', this.currentBundle.quantity);
    for (let i = 0; i < this.currentBundle.quantity; i++) {
      if (!this.currentBundle.items[i]) {
        this.currentBundle.items[i] = {};
      }
        
      // Update options based on new variant
      currentOptions.forEach((option, index) => {
        const variantOptionValue = variant[`option${index + 1}`];
        if (variantOptionValue) {
          this.currentBundle.items[i][option.name] = variantOptionValue;
        }
      });
        
      // Update the variant ID
      this.currentBundle.items[i].variantId = variant.id;
    }
      
    // Update all bundle pricing displays
    this.updateBundlePricing();
      
    // Update customization UI if visible
    const customization = document.getElementById('bundleCustomization');
    if (customization && customization.style.display !== 'none') {
      const itemsContainer = document.getElementById('bundleItems');
      this.generateCustomizationItems(itemsContainer);
    }
      
    // Update add to cart button
    this.updateAddToCartButton();
      
    this.debug('Updated bundle for variant:', variant.id);
    this.debug('Bundle items after variant change:');
    this.currentBundle.items.forEach((item, index) => {
      this.debug(`  Item ${index}:`, {
        variantId: item.variantId,
        Color: item.Color,
        Size: item.Size,
        allProps: item
      });
    });
    this.debug('=== handleVariantChange END ===');
  }
  
  updateBundle(quantity, discount = null) {
    this.debug('=== updateBundle START ===');
    this.debug(`Changing quantity from ${this.currentBundle.quantity} to ${quantity}`);
    if (this.debugMode) this.debug('Current items before update:', JSON.parse(JSON.stringify(this.currentBundle.items)));
      
    // Find the discount for this quantity if not provided
    if (discount === null) {
      const tier = this.pricingTiers.find(t => t.quantity === quantity);
      discount = tier ? tier.discount : 0;
    }
      
    // Get current variant to preserve selection
    const currentVariantId = this.variantIdInputEl ? this.variantIdInputEl.value : null;
    this.debug('Current form variant ID:', currentVariantId);
      
    this.currentBundle.quantity = quantity;
    // Find the tier to check for absolute price
    const tier = this.pricingTiers.find(t => t.quantity === quantity);
    this.currentBundle.discountedPrice = this.calculatePrice(discount, tier);
      
    // Clear and rebuild the items array when quantity changes
    const previousLength = this.currentBundle.items.length;
    this.currentBundle.items = []; // Clear the array
      
    this.debug(`Clearing items array (was ${previousLength} items, will be ${quantity} items)`);
      
    // Always sync with current variant to rebuild the items array
    if (currentVariantId) {
      this.syncWithCurrentVariant();
    }
  
    // Update UI
    this.updateBundleSelection(quantity);
    this.updateBundlePricing();
    this.updateCustomization();
    this.updateBundleTitle(quantity);
    this.updateAddToCartButton();
  
    this.debug('Bundle updated:', this.currentBundle);
    if (this.debugMode) this.debug('Items after update:', JSON.parse(JSON.stringify(this.currentBundle.items)));
    this.debug('=== updateBundle END ===');
  }
  
  updateBundleTitle(quantity) {
    const titleElement = document.querySelector('.bundle-title .bundle-subtitle');
    if (titleElement) {
      const tier = this.pricingTiers.find(t => t.quantity === quantity);
      const label = tier ? tier.label : `${quantity} ITEM${quantity > 1 ? 'S' : ''}`;
      titleElement.textContent = label;
    }
  }
  
  updateAddToCartButton() {
    // Use requestAnimationFrame to defer non-critical button updates for better INP
    window.requestAnimationFrame(() => {
      // Use the add to cart button cached at setup - but NOT in the cart drawer
      const addToCartBtn = this.addToCartBtn;

      if (addToCartBtn) {
        // Round per-unit to whole cents BEFORE multiplying so the displayed
        // total matches Shopify's cart, which rounds each line item to cents.
        // Without this, $49.95 × 0.9 = $44.955 stays sub-cent and × 2 prints
        // $89.91, while the cart prints $89.92 (4496¢ × 2).
        const unitCents = Math.round(this.currentBundle.discountedPrice * 100);
        const total = (unitCents * this.currentBundle.quantity) / 100;
        const retailTotal = this.currentBundle.basePrice * this.currentBundle.quantity;
        const hasDiscount = total < retailTotal;
        const originalText = addToCartBtn.getAttribute('data-original-text') || addToCartBtn.textContent;

        // Store original text if not already stored
        if (!addToCartBtn.getAttribute('data-original-text')) {
          addToCartBtn.setAttribute('data-original-text', originalText);
        }

        // Build button HTML based on whether there's a discount
        let buttonHTML = '';
        if (hasDiscount) {
          // Show strikethrough retail price and discounted price
          buttonHTML = `
            <span style="font-weight: bold;">ADD TO CART - </span>
            <span style="text-decoration: line-through; font-weight: normal; opacity: 0.7;">$${retailTotal.toFixed(2)}</span>
            <span style="font-weight: bold; margin-left: 8px;">$${total.toFixed(2)}</span>
            <span style="font-weight: bold; margin-left: 8px;">→</span>
          `;
        } else {
          // Just show regular price
          buttonHTML = `<span style="font-weight: bold;">ADD TO CART - $${total.toFixed(2)} <span style="margin-left: 8px;">→</span></span>`;
        }

        addToCartBtn.innerHTML = buttonHTML;

        // Dispatch custom event for sticky add-to-cart to sync
        document.dispatchEvent(new CustomEvent('bundle:updated', {
          detail: { quantity: this.currentBundle.quantity, total, retailTotal }
        }));
      }
    });
  }
  
  updateBundleSelection(quantity) {
    // Use requestAnimationFrame to batch visual updates
    window.requestAnimationFrame(() => {
      // Update selected state
      this.bundleOptionElements.forEach(option => {
        const optionQuantity = parseInt(option.dataset.quantity);
        if (optionQuantity === quantity) {
          option.setAttribute('data-selected', 'true');
        } else {
          option.removeAttribute('data-selected');
        }
      });
    });
  }
  
  updateBundlePricing() {
    // Use requestAnimationFrame to batch DOM updates for better INP
    window.requestAnimationFrame(() => {
      // Update price per item displays
      this.bundleOptionElements.forEach(option => {
        const quantity = parseInt(option.dataset.quantity);
        const discount = parseFloat(option.dataset.discount);
        // Find the corresponding tier to check for absolute price
        const tier = this.pricingTiers.find(t => t.quantity === quantity);
        const pricePerItem = this.calculatePrice(discount, tier);

        const priceElement = option.querySelector('.bundle-price');
        if (priceElement) {
          priceElement.textContent = `$${pricePerItem.toFixed(2)}/${this.unitType.lowercase.singular}`;
        }
      });
    });

    // Update add to cart button will be called separately
    this.updateAddToCartButton();
  }
  
  updateCustomization() {
    const customization = document.getElementById('bundleCustomization');
    const itemsContainer = document.getElementById('bundleItems');
  
    if (this.currentBundle.quantity > 1) {
      customization.style.display = 'block';
      this.generateCustomizationItems(itemsContainer);
    } else {
      customization.style.display = 'none';
    }
  }
  
  generateCustomizationItems(container) {
    if (!container) return;
  
    container.innerHTML = '';
      
    for (let i = 1; i <= this.currentBundle.quantity; i++) {
      // Get available options for this item
      const availableOptions = this.getAvailableOptionsForItem(i);
        
      // Initialize item with default selections if not already set
      if (!this.currentBundle.items[i - 1]) {
        this.currentBundle.items[i - 1] = {};
        availableOptions.forEach(option => {
          this.currentBundle.items[i - 1][option.name] = option.selected;
        });
        // Find and set the variant ID for default selection
        const variantId = this.findVariantForItem(i);
        if (variantId) {
          this.currentBundle.items[i - 1].variantId = variantId;
        }
      }
        
      const itemRow = document.createElement('div');
      itemRow.className = 'bundle-item-row';
      itemRow.innerHTML = `
          <div class="item-number">${this.unitType.singular} ${i}:</div>
          <div class="item-selects">
            ${availableOptions.map(option => {
    // Properly escape the option name for use in JavaScript string
    const escapedName = option.name
      .replace(/\\/g, '\\\\')  // Escape backslashes first
      .replace(/'/g, '\\\'')    // Escape single quotes
      .replace(/"/g, '\\"')    // Escape double quotes
      .replace(/\n/g, '\\n')   // Escape newlines
      .replace(/\r/g, '\\r')   // Escape carriage returns
      .replace(/\t/g, '\\t');  // Escape tabs
              
    // Get the selected value for this item
    const selectedValue = this.currentBundle.items[i - 1][option.name] || option.selected;

    // Check if this is a color option
    const isColorOption = option.name.toLowerCase() === 'color';

    if (isColorOption) {
      // Custom dropdown with color swatches
      const selectedImage = this.getVariantImageForColor(selectedValue);
      return `
        <div class="color-dropdown-wrapper" data-item="${i}" data-option="${option.name}">
          <button type="button" class="color-dropdown-trigger" onclick="bundlePricing.toggleColorDropdown(${i})">
            <span class="color-dropdown-selected">
              ${selectedImage ? `<img src="${selectedImage}" alt="${selectedValue}" class="color-swatch-img" width="100" height="100">` : '<span class="color-swatch-fallback"></span>'}
              <span class="color-dropdown-value">${selectedValue}</span>
            </span>
            <svg class="color-dropdown-arrow" width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="color-dropdown-menu" id="colorMenu-${i}">
            ${option.values.map(value => {
    const img = this.getVariantImageForColor(value);
    return `
                <button type="button" class="color-dropdown-option ${value === selectedValue ? 'selected' : ''}"
                        onclick="bundlePricing.selectColorOption(${i}, '${escapedName}', '${value.replace(/'/g, '\\\'')}')">
                  ${img ? `<img src="${img}" alt="${value}" class="color-swatch-img" width="100" height="100">` : '<span class="color-swatch-fallback"></span>'}
                  <span class="color-option-name">${value}</span>
                  ${value === selectedValue ? '<svg class="color-check" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' : ''}
                </button>
              `;
  }).join('')}
          </div>
        </div>
      `;
    } else {
      // Standard select for non-color options
      return `
                <select class="item-select"
                        data-item="${i}"
                        data-option="${option.name}"
                        onchange="bundlePricing.updateItemOption(${i}, '${escapedName}', this.value)">
                  ${option.values.map(value => `
                    <option value="${value}" ${value === selectedValue ? 'selected' : ''}>${value}</option>
                  `).join('')}
                </select>
              `;
    }
  }).join('')}
          </div>
        `;
      container.appendChild(itemRow);
    }

    // Close dropdowns when clicking outside
    this.setupColorDropdownListeners();
  }

  getVariantImageForColor(colorValue) {
    // Get variant image for a specific color value
    if (!this.variantsData || !this.variantsData.length) return null;

    try {
      const variants = this.variantsData;
      // Find a variant with this color that has an image
      const variantWithImage = variants.find(v => {
        const hasColor = v.option1 === colorValue || v.option2 === colorValue || v.option3 === colorValue;
        return hasColor && v.featured_image && v.featured_image.src;
      });

      if (variantWithImage && variantWithImage.featured_image) {
        // Return a small version of the image for the swatch
        return variantWithImage.featured_image.src.replace(/(\.[^.]+)$/, '_100x$1');
      }
      return null;
    } catch (e) {
      this.debug('Error getting variant image:', e);
      return null;
    }
  }

  toggleColorDropdown(itemIndex) {
    const menu = document.getElementById(`colorMenu-${itemIndex}`);
    if (!menu) return;

    const isOpen = menu.classList.contains('open');

    // Close all other dropdowns first
    document.querySelectorAll('.color-dropdown-menu.open').forEach(m => {
      m.classList.remove('open');
    });

    // Toggle this one
    if (!isOpen) {
      menu.classList.add('open');
    }
  }

  selectColorOption(itemIndex, optionName, value) {
    // Update the selection
    this.updateItemOption(itemIndex, optionName, value);

    // Close the dropdown
    const menu = document.getElementById(`colorMenu-${itemIndex}`);
    if (menu) {
      menu.classList.remove('open');
    }

    // Update the trigger display
    const wrapper = document.querySelector(`.color-dropdown-wrapper[data-item="${itemIndex}"]`);
    if (wrapper) {
      const selectedImage = this.getVariantImageForColor(value);
      const trigger = wrapper.querySelector('.color-dropdown-selected');
      if (trigger) {
        trigger.innerHTML = `
          ${selectedImage ? `<img src="${selectedImage}" alt="${value}" class="color-swatch-img" width="100" height="100">` : '<span class="color-swatch-fallback"></span>'}
          <span class="color-dropdown-value">${value}</span>
        `;
      }

      // Update selected state in menu
      wrapper.querySelectorAll('.color-dropdown-option').forEach(opt => {
        const optValue = opt.querySelector('.color-option-name').textContent;
        const isSelected = optValue === value;
        opt.classList.toggle('selected', isSelected);

        // Update checkmark
        const existingCheck = opt.querySelector('.color-check');
        if (isSelected && !existingCheck) {
          opt.insertAdjacentHTML('beforeend', '<svg class="color-check" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>');
        } else if (!isSelected && existingCheck) {
          existingCheck.remove();
        }
      });
    }
  }

  setupColorDropdownListeners() {
    // Remove existing listener if any
    if (this._colorDropdownListener) {
      document.removeEventListener('click', this._colorDropdownListener);
    }

    // Add click outside listener
    this._colorDropdownListener = (e) => {
      if (!e.target.closest('.color-dropdown-wrapper')) {
        document.querySelectorAll('.color-dropdown-menu.open').forEach(m => {
          m.classList.remove('open');
        });
      }
    };
    document.addEventListener('click', this._colorDropdownListener);
  }

  getCurrentVariantOptions() {
    const options = [];
      
    // Get options from enhanced variant picker
    const optionContainers = this.optionContainerElements || [];

    optionContainers.forEach((container, index) => {
      const label = container.querySelector('.form__label');
      const selectedInput = container.querySelector('input:checked');
      const allInputs = container.querySelectorAll('input[type="radio"]');
        
      if (label && selectedInput) {
        // Get the raw label text
        const rawText = label.textContent;
          
        // Clean the option name - handle newlines and extract the option name
        let optionName = rawText.trim();
          
        // If there's a newline, take the first part
        if (optionName.includes('\n')) {
          optionName = optionName.split('\n')[0].trim();
        }
          
        // Remove the selected value display (e.g., "Color - Red" -> "Color")
        const dashIndex = optionName.indexOf(' -');
        if (dashIndex > 0) {
          optionName = optionName.substring(0, dashIndex).trim();
        }
          
        this.debug(`Option ${index}: Raw="${rawText}" Clean="${optionName}"`);
          
        const values = Array.from(allInputs).map(input => input.value);
          
        options.push({
          name: optionName,
          selected: selectedInput.value,
          values: values,
          position: index // Store position for later use
        });
      }
    });
      
    return options;
  }
    
  getAvailableOptionsForItem(itemIndex, changedOption = null) {
    // Get all available variants
    if (!this.variantsData || !this.variantsData.length) return this.getCurrentVariantOptions();

    try {
      const variants = this.variantsData;
      const availableVariants = variants.filter(v => v.available);
        
      if (availableVariants.length === 0) {
        return this.getCurrentVariantOptions();
      }
        
      const currentOptions = this.getCurrentVariantOptions();
      const item = this.currentBundle.items[itemIndex - 1] || {};
        
      // For each option, determine which values are available
      return currentOptions.map(option => {
        const availableValues = new Set();
          
        // Check each available variant
        availableVariants.forEach(variant => {
          // Check if this variant matches other selected options
          let matches = true;
            
          currentOptions.forEach(otherOption => {
            if (otherOption.name !== option.name) {
              const selectedValue = item[otherOption.name] || otherOption.selected;
              const variantValue = variant[`option${otherOption.position + 1}`];
                
              if (variantValue && variantValue !== selectedValue) {
                matches = false;
              }
            }
          });
            
          if (matches) {
            const value = variant[`option${option.position + 1}`];
            if (value) {
              availableValues.add(value);
            }
          }
        });
          
        // Filter the option values to only include available ones
        const filteredValues = option.values.filter(value => availableValues.has(value));
          
        return {
          ...option,
          values: filteredValues.length > 0 ? filteredValues : option.values,
          availableValues: Array.from(availableValues)
        };
      });
    } catch (e) {
      this.debug('Error getting available options:', e);
      return this.getCurrentVariantOptions();
    }
  }
  
  updateItemOption(itemIndex, optionName, value) {
    // Store individual item customization
    if (!this.currentBundle.items[itemIndex - 1]) {
      this.currentBundle.items[itemIndex - 1] = {};
    }
      
    this.currentBundle.items[itemIndex - 1][optionName] = value;
      
    // Find the variant ID for this item's selection
    const variantId = this.findVariantForItem(itemIndex);
    if (variantId) {
      this.currentBundle.items[itemIndex - 1].variantId = variantId;
    }
      
    this.debug(`Item ${itemIndex} ${optionName}: ${value}, Variant ID: ${variantId}`);
      
    // Update available options for this item
    this.updateItemDropdowns(itemIndex);
  }
    
  updateItemDropdowns(itemIndex) {
    // Get the item row
    const itemRows = document.querySelectorAll('.bundle-item-row');
    const itemRow = itemRows[itemIndex - 1];
    if (!itemRow) return;

    // Get available options for this item
    const availableOptions = this.getAvailableOptionsForItem(itemIndex);
    const item = this.currentBundle.items[itemIndex - 1] || {};

    // Update each dropdown (both standard selects and custom color dropdowns)
    availableOptions.forEach(option => {
      const isColorOption = option.name.toLowerCase() === 'color';

      if (isColorOption) {
        // Update custom color dropdown
        const wrapper = itemRow.querySelector(`.color-dropdown-wrapper[data-option="${CSS.escape(option.name)}"]`);
        if (!wrapper) return;

        const selectedValue = item[option.name] || option.selected;
        const selectedImage = this.getVariantImageForColor(selectedValue);

        // Update trigger display
        const trigger = wrapper.querySelector('.color-dropdown-selected');
        if (trigger) {
          trigger.innerHTML = `
            ${selectedImage ? `<img src="${selectedImage}" alt="${selectedValue}" class="color-swatch-img" width="100" height="100">` : '<span class="color-swatch-fallback"></span>'}
            <span class="color-dropdown-value">${selectedValue}</span>
          `;
        }

        // Update menu options
        const menu = wrapper.querySelector('.color-dropdown-menu');
        if (menu) {
          const escapedName = option.name.replace(/'/g, '\\\'');
          menu.innerHTML = option.values.map(value => {
            const img = this.getVariantImageForColor(value);
            return `
              <button type="button" class="color-dropdown-option ${value === selectedValue ? 'selected' : ''}"
                      onclick="bundlePricing.selectColorOption(${itemIndex}, '${escapedName}', '${value.replace(/'/g, '\\\'')}')">
                ${img ? `<img src="${img}" alt="${value}" class="color-swatch-img" width="100" height="100">` : '<span class="color-swatch-fallback"></span>'}
                <span class="color-option-name">${value}</span>
                ${value === selectedValue ? '<svg class="color-check" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' : ''}
              </button>
            `;
          }).join('');
        }
      } else {
        // Update standard select dropdown
        const selector = `select[data-option="${CSS.escape(option.name)}"]`;
        const select = itemRow.querySelector(selector);
        if (!select) return;

        const currentValue = select.value;
        const selectedValue = item[option.name] || currentValue;

        // Clear and rebuild options
        select.innerHTML = option.values.map(value => `
            <option value="${value}" ${value === selectedValue ? 'selected' : ''}>${value}</option>
          `).join('');

        // If the current value is no longer available, update the item
        if (!option.values.includes(currentValue) && option.values.length > 0) {
          const newValue = option.values.includes(selectedValue) ? selectedValue : option.values[0];
          select.value = newValue;
          item[option.name] = newValue;

          // Update variant ID with new selection
          const variantId = this.findVariantForItem(itemIndex);
          if (variantId) {
            item.variantId = variantId;
          }
        }
      }
    });
  }
    
  findVariantForItem(itemIndex) {
    const item = this.currentBundle.items[itemIndex - 1];
    if (!item) return null;
      
    // Get product variants data
    if (!this.variantsData || !this.variantsData.length) return null;

    try {
      const variants = this.variantsData;

      // Find matching variant based on selected options
      const matchingVariant = variants.find(variant => {
        // Check if all options match
        return Object.entries(item).every(([optionName, value]) => {
          if (optionName === 'variantId') return true; // Skip variantId property
            
          // Find which option position this is (option1, option2, or option3)
          const optionIndex = this.getOptionIndex(optionName);
          if (optionIndex === -1) return true; // Skip if not a variant option
            
          return variant[`option${optionIndex + 1}`] === value;
        });
      });
        
      return matchingVariant ? matchingVariant.id : null;
    } catch (e) {
      this.debug('Error finding variant:', e);
      return null;
    }
  }
    
  getOptionIndex(optionName) {
    // Map option names to their index position
    const optionContainers = this.optionContainerElements || [];

    for (let i = 0; i < optionContainers.length; i++) {
      const label = optionContainers[i].querySelector('.form__label');
      if (label) {
        // Clean the label text the same way as in getCurrentVariantOptions
        let labelText = label.textContent.trim();
          
        // If there's a newline, take the first part
        if (labelText.includes('\n')) {
          labelText = labelText.split('\n')[0].trim();
        }
          
        // Remove the selected value display
        const dashIndex = labelText.indexOf(' -');
        if (dashIndex > 0) {
          labelText = labelText.substring(0, dashIndex).trim();
        }
          
        if (labelText === optionName) {
          return i;
        }
      }
    }
      
    return -1;
  }
  
  toggleCustomization() {
    const itemsContainer = document.getElementById('bundleItems');
    const collapseText = document.querySelector('.collapse-text');
    const collapseButton = document.querySelector('.bundle-customization-header .collapse-btn');
    if (!itemsContainer) return;

    const expand = itemsContainer.style.display === 'none';
    itemsContainer.style.display = expand ? 'block' : 'none';
    if (collapseText) collapseText.textContent = expand ? 'Collapse' : 'Expand';
    if (collapseButton) collapseButton.setAttribute('aria-expanded', expand ? 'true' : 'false');
  }
    
  syncWithCurrentVariant() {
    this.debug('=== syncWithCurrentVariant START ===');
    if (this.debugMode) this.debug('Items before sync:', JSON.parse(JSON.stringify(this.currentBundle.items)));
      
    // Get the current variant ID from the form
    const variantIdInput = this.variantIdInputEl;
    if (!variantIdInput || !variantIdInput.value) {
      this.debug('No variant ID found during sync');
      return;
    }

    const currentVariantId = variantIdInput.value;
    this.debug('Syncing with variant ID:', currentVariantId);

    // Get the full variant data
    if (!this.variantsData || !this.variantsData.length) {
      this.debug('No product JSON found during sync');
      return;
    }

    try {
      const variants = this.variantsData;
      const currentVariant = variants.find(v => v.id.toString() === currentVariantId);
        
      if (currentVariant) {
        this.debug('Syncing with current variant:', currentVariant);
          
        // Update base price
        this.currentBundle.basePrice = currentVariant.price / 100;
          
        // Initialize all bundle items with current variant options
        const currentOptions = this.getCurrentVariantOptions();
        for (let i = 0; i < this.currentBundle.quantity; i++) {
          if (!this.currentBundle.items[i]) {
            this.currentBundle.items[i] = {};
          }
            
          // Set options based on current variant
          currentOptions.forEach((option, index) => {
            const variantOptionValue = currentVariant[`option${index + 1}`];
            if (variantOptionValue) {
              this.currentBundle.items[i][option.name] = variantOptionValue;
            }
          });
            
          // Set the variant ID
          this.currentBundle.items[i].variantId = currentVariant.id;
        }
          
        // Update pricing displays
        this.updateBundlePricing();
          
        this.debug('Bundle items initialized with variant:', this.currentBundle.items);
        this.debug('=== syncWithCurrentVariant END ===');
      }
    } catch (e) {
      this.debug('Error syncing with current variant:', e);
    }
  }
  
  // Force refresh bundle with current variant before getting cart data
  refreshBundleFromForm() {
    this.debug('=== refreshBundleFromForm START ===');
      
    const currentVariantId = this.variantIdInputEl ? this.variantIdInputEl.value : null;

    if (currentVariantId && this.currentBundle.quantity === 1) {
      this.debug('Single item - forcing refresh with current form variant:', currentVariantId);
        
      // Clear and rebuild with current variant
      this.currentBundle.items = [];
      this.syncWithCurrentVariant();
    }
      
    this.debug('=== refreshBundleFromForm END ===');
  }
    
  // Method to integrate with existing product form submission
  getBundleCartData() {
    const bundleId = this.generateBundleId();
      
    this.debug('=== getBundleCartData START ===');
    this.debug('Current bundle state:', {
      quantity: this.currentBundle.quantity,
      items: this.currentBundle.items
    });
      
    // Note: Bundle discounts are applied via Shopify automatic discounts
    // The discounts will be shown at checkout based on quantity thresholds
      
    // Get current selected variant ID as default
    const allVariantInputs = document.querySelectorAll('input[name="id"]');
    this.debug(`Found ${allVariantInputs.length} variant ID inputs on page`);
    allVariantInputs.forEach((input, index) => {
      this.debug(`Variant input ${index}:`, {
        value: input.value,
        form: input.form?.id || 'no-form-id',
        formClass: input.form?.className || 'no-form-class',
        disabled: input.disabled,
        type: input.type,
        closestProductForm: input.closest('product-form') ? 'yes' : 'no'
      });
    });
      
    // Be more specific - look for the variant input inside the product form
    const variantIdInput = this.variantIdInputEl;
    const defaultVariantId = variantIdInput ? variantIdInput.value : null;
      
    this.debug('Using variant ID input:', {
      element: variantIdInput,
      value: defaultVariantId,
      form: variantIdInput ? variantIdInput.form : null
    });
      
    if (!defaultVariantId) {
      this.debug('No default variant ID found');
      return [];
    }
      
    // Group items by variant ID for hybrid approach
    const variantGroups = new Map();
      
    // Special handling for single item - always use the current form variant
    if (this.currentBundle.quantity === 1) {
      this.debug(`Single item bundle - using form variant ID: ${defaultVariantId}`);
      variantGroups.set(defaultVariantId, {
        id: defaultVariantId,
        quantity: 1,
        options: {}
      });
    } else {
      // Count quantities for each variant
      for (let i = 0; i < this.currentBundle.quantity; i++) {
        const item = this.currentBundle.items[i] || {};
        const variantId = item.variantId || defaultVariantId;
          
        this.debug(`Item ${i}: variantId=${variantId}, defaultVariantId=${defaultVariantId}, item=`, item);
          
        if (variantGroups.has(variantId)) {
          variantGroups.get(variantId).quantity += 1;
        } else {
          variantGroups.set(variantId, {
            id: variantId,
            quantity: 1,
            options: item // Store the options for reference
          });
        }
      }
    }
      
    // Convert to array format for cart
    const items = [];
    variantGroups.forEach((group, variantId) => {
      const itemData = {
        id: variantId,
        quantity: group.quantity,
        properties: {
          '_bundle_id': bundleId,
          '_bundle_total_items': this.currentBundle.quantity
          // Removed individual item properties since we're grouping
        }
      };
        
      this.debug(`Grouped variant ${variantId}: quantity ${group.quantity}`);
      items.push(itemData);
    });
      
    this.debug('Bundle cart data prepared (grouped):', items);
    this.debug(`Total variants: ${items.length}, Total items: ${this.currentBundle.quantity}`);
    this.debug('=== getBundleCartData END ===');
      
    return items;
  }
  
  generateBundleId() {
    return 'bundle_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }
  
  getBundleDiscountPercent() {
    const tier = this.pricingTiers.find(t => t.quantity === this.currentBundle.quantity);
    const discountPercent = tier ? Math.round(tier.discount * 100) : 0;
    this.debug(`Bundle discount for ${this.currentBundle.quantity} items: ${discountPercent}% (tier: ${tier ? tier.label : 'none'})`);
    return discountPercent;
  }
  
  // ---------------------------------------------------------------------------
  // Pack mode: "Choose your pack" picker (Helios Buy 3 Get 1 Free,
  // Asana #1219032946047754).
  //
  // Enabled when BundlePricingData.packMode is set (snippets/wr-pack-mode.liquid)
  // and the server-rendered [data-wr-pack] slot exists. Tier shape, from the
  // bundle.pricing_tiers metafield ("layout": "pack"):
  //   { quantity, discount, freeUnits?, label, badge?, perk?, isBest? }
  // A tier with freeUnits charges for (quantity - freeUnits) units. The free
  // unit comes from the store's automatic Buy X Get Y discount, which does not
  // add items by itself, so every unit (including the free one) goes in the cart.
  // Money math is in integer cents. INP: handlers only touch nodes cached in
  // setupPack(); nothing sweeps the document per interaction.
  // ---------------------------------------------------------------------------

  loadPackPreviewOverride() {
    // PREVIEW ONLY: tier JSON from theme settings (see snippets/wr-pack-mode.liquid).
    // A missing or malformed override leaves the metafield tiers in place.
    const overrideElement = document.getElementById('WrPackPreviewTiers-' + this.getSectionId());
    if (!overrideElement) return;
    try {
      const override = JSON.parse(overrideElement.textContent);
      if (!override || !Array.isArray(override.pricingTiers) || override.pricingTiers.length === 0) return;
      this.pricingTiers = override.pricingTiers;
      this.defaultSelection = override.defaultSelection ?? null;
      if (override.unitType) this.unitType = override.unitType;
      this.updateTierLabels();
    } catch (e) {
      this.debug('Invalid pack preview override, keeping metafield tiers:', e);
    }
  }

  setupPack() {
    this.isPack = true;
    this.packMixOpen = false;

    // No headline price above the picker in pack mode: each pack row carries its
    // per-shirt price and total, and the add-to-cart button repeats the total.
    const root = document.querySelector('[data-wr-pack]');
    const mixElement = document.querySelector('[data-wr-pack-mix]');
    this.packEls = {
      options: root.querySelector('[data-wr-pack-options]'),
      ctaSub: document.querySelector('[data-wr-pack-cta-sub]'),
      atcText: this.addToCartBtn ? this.addToCartBtn.querySelector('[data-atc-text]') : null,
      mix: mixElement,
      mixToggle: mixElement ? mixElement.querySelector('[data-wr-pack-mix-toggle]') : null,
      mixLabel: mixElement ? mixElement.querySelector('[data-wr-pack-mix-label]') : null,
      mixIcon: mixElement ? mixElement.querySelector('[data-wr-pack-mix-icon]') : null,
      mixRows: mixElement ? mixElement.querySelector('[data-wr-pack-mix-rows]') : null,
      optionRows: [],
      suffixes: []
    };

    this.packTiers = this.pricingTiers
      .filter((tier) => this.isValidPackTier(tier))
      .sort((a, b) => a.quantity - b.quantity);
    if (this.packTiers.length === 0) {
      this.packTiers = [{ quantity: 1, discount: 0, label: '1 ' + this.getPackNoun(1, true) }];
    }
    // Legacy helpers (getDefaultSelection, getBundleDiscountPercent) read pricingTiers.
    this.pricingTiers = this.packTiers;

    this.indexPackVariants();
    this.packOptions = this.readPackOptions();
    const selectedId = this.variantIdInputEl ? String(this.variantIdInputEl.value) : '';
    this.packMainVariant = this.packVariantById.get(selectedId) ||
      (this.variantsData && this.variantsData.length ? this.variantsData[0] : null);

    this.currentBundle.quantity = this.getDefaultSelection();
    this.currentBundle.items = [];
    this.fillPackItems();

    this.renderPackOptions();
    this.addPackLabelSuffixes();
    this.bindPackEvents();
    this.renderPackState();

    if (this.addToCartBtn) this.addToCartBtn.classList.add('is-ready');
  }

  isValidPackTier(tier) {
    if (!tier || !Number.isInteger(tier.quantity) || tier.quantity < 1) return false;
    const discount = tier.discount || 0;
    const freeUnits = tier.freeUnits || 0;
    return discount >= 0 && discount < 1 &&
      Number.isInteger(freeUnits) && freeUnits >= 0 && freeUnits < tier.quantity;
  }

  indexPackVariants() {
    this.packVariantById = new Map();
    this.packVariantByKey = new Map();
    (this.variantsData || []).forEach((variant) => {
      this.packVariantById.set(String(variant.id), variant);
      this.packVariantByKey.set(this.getPackVariantValues(variant).join('\u0000'), variant);
    });
  }

  getPackVariantValues(variant) {
    if (Array.isArray(variant.options)) return variant.options;
    return [variant.option1, variant.option2, variant.option3].filter((value) => value !== null && value !== undefined);
  }

  readPackOptions() {
    // Option position comes from the picker's data-index="optionN" so the
    // mapping holds even if an option container is not rendered.
    return (this.optionContainerElements || []).map((container) => {
      const inputs = Array.from(container.querySelectorAll('input[type="radio"]'));
      const label = container.querySelector('.form__label');
      const nameNode = label ? label.firstChild : null;
      const name = nameNode && nameNode.nodeType === Node.TEXT_NODE ? nameNode.textContent.trim() : '';
      const position = inputs.length ? parseInt(String(inputs[0].dataset.index || '').replace('option', ''), 10) : NaN;
      return {
        name,
        label,
        index: position - 1,
        values: inputs.map((input) => input.value),
        isColor: container.classList.contains('color-option')
      };
    }).filter((option) => option.name && option.index >= 0 && option.values.length > 0);
  }

  getPackNoun(quantity, capitalized = false) {
    const lowercase = this.unitType.lowercase || {};
    const noun = quantity === 1 ? (lowercase.singular || 'item') : (lowercase.plural || 'items');
    return capitalized ? noun.charAt(0).toUpperCase() + noun.slice(1) : noun;
  }

  getPackTier(quantity) {
    return this.packTiers.find((tier) => tier.quantity === quantity) || { quantity, discount: 0 };
  }

  getPackTotals(quantity, unitPrices) {
    const tier = this.getPackTier(quantity);
    const wasCents = unitPrices.reduce((sum, price) => sum + price, 0);
    const freeUnits = tier.freeUnits || 0;
    let totalCents;
    if (freeUnits > 0) {
      // Buy X Get Y makes the lowest-priced units free.
      const freeCents = unitPrices.slice().sort((a, b) => a - b).slice(0, freeUnits)
        .reduce((sum, price) => sum + price, 0);
      totalCents = wasCents - freeCents;
    } else {
      // Shopify's percentage discount rounds each unit to whole cents (2 x $49.95
      // at 10% = 2 x $44.96 = $89.92 in the cart), so round per unit to match.
      totalCents = unitPrices.reduce((sum, price) => sum + Math.round(price * (1 - (tier.discount || 0))), 0);
    }
    return { tier, totalCents, wasCents, saveCents: wasCents - totalCents };
  }

  formatPackMoney(cents) {
    const fixed = (Math.round(cents) / 100).toFixed(2);
    const [whole, fraction] = fixed.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const format = String(this.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    const noDecimals = String(Math.round(cents / 100));
    const amounts = {
      amount: grouped + '.' + fraction,
      amount_no_decimals: noDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ','),
      amount_with_comma_separator: whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + fraction,
      amount_no_decimals_with_comma_separator: noDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => amounts[key] || amounts.amount);
  }

  escapePackText(value) {
    const entities = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' };
    return String(value).replace(/[&<>"']/g, (character) => entities[character]);
  }

  makePackItem(variant) {
    return variant
      ? { variantId: variant.id, values: this.getPackVariantValues(variant).slice() }
      : { variantId: null, values: [] };
  }

  fillPackItems() {
    // Same color & size for every shirt unless the per-shirt rows are open, in
    // which case existing rows keep their choices and new rows copy the picker.
    const quantity = this.currentBundle.quantity;
    const items = this.packMixOpen ? this.currentBundle.items.slice(0, quantity) : [];
    while (items.length < quantity) items.push(this.makePackItem(this.packMainVariant));
    this.currentBundle.items = items;
  }

  renderPackOptions() {
    const container = this.packEls.options;
    if (!container) return;
    const groupName = 'wr-pack-' + this.getSectionId();
    const unitNoun = this.escapePackText(this.getPackNoun(1));
    const shipping = this.freeShippingThresholdCents > 0
      ? `<span class="wr-pack__ship" data-wr-pack-row-ship hidden><strong>FREE SHIPPING</strong> · US orders ${this.escapePackText(this.formatPackThreshold())}</span>`
      : '';
    container.innerHTML = this.packTiers.map((tier) => {
      const freeUnits = tier.freeUnits || 0;
      // A free-unit tier leads with the offer ("BUY 3, GET 1 FREE") and states the
      // pack size underneath; other tiers keep their label ("2 Shirts").
      const label = freeUnits > 0
        ? `BUY ${tier.quantity - freeUnits}, GET ${freeUnits} FREE`
        : tier.label || tier.quantity + ' ' + this.getPackNoun(tier.quantity, true);
      const count = freeUnits > 0
        ? `<span class="wr-pack__count">${tier.quantity} ${this.escapePackText(this.getPackNoun(tier.quantity))} total</span>`
        : '';
      const tag = tier.badge
        ? `<span class="wr-pack__tag${tier.isBest ? ' wr-pack__tag--best' : ''}">${this.escapePackText(tier.badge)}</span>`
        : '';
      const perk = tier.perk
        ? `<span class="wr-pack__perk"><span aria-hidden="true">&#10003; </span>${this.escapePackText(tier.perk)}</span>`
        : '';
      return `
        <label class="wr-pack__option${freeUnits > 0 ? ' wr-pack__option--deal' : ''}" data-quantity="${tier.quantity}">
          <input class="wr-pack__input" type="radio" name="${groupName}" value="${tier.quantity}">
          <span class="wr-pack__dot" aria-hidden="true"></span>
          <span class="wr-pack__body">
            <span class="wr-pack__title"><span class="wr-pack__label">${this.escapePackText(label)}</span>${tag}</span>
            ${count}
            ${perk}
            ${shipping}
          </span>
          <span class="wr-pack__prices">
            <span class="wr-pack__each"><span data-wr-pack-row-each></span><span class="wr-pack__each-unit">/${unitNoun}</span></span>
            <span class="wr-pack__sum">Total <span class="wr-pack__total" data-wr-pack-row-total></span></span>
          </span>
        </label>`;
    }).join('');

    this.packEls.optionRows = Array.from(container.querySelectorAll('.wr-pack__option')).map((row) => ({
      row,
      quantity: parseInt(row.dataset.quantity, 10),
      input: row.querySelector('.wr-pack__input'),
      each: row.querySelector('[data-wr-pack-row-each]'),
      total: row.querySelector('[data-wr-pack-row-total]'),
      ship: row.querySelector('[data-wr-pack-row-ship]')
    }));
  }

  /** Free-shipping threshold for display, e.g. "$99+" (whole amounts drop ".00"). */
  formatPackThreshold() {
    const cents = this.freeShippingThresholdCents;
    const money = this.formatPackMoney(cents);
    return (cents % 100 === 0 ? money.replace(/[.,]00(?!\d)/, '') : money) + '+';
  }

  qualifiesForPackFreeShipping(totalCents) {
    return this.freeShippingThresholdCents > 0 && totalCents >= this.freeShippingThresholdCents;
  }

  addPackLabelSuffixes() {
    // "Color - Blue Camo (all 4 shirts)": one choice applies to every shirt.
    this.packEls.suffixes = this.packOptions
      .filter((option) => option.label)
      .map((option) => {
        const suffix = document.createElement('span');
        suffix.className = 'wr-pack-all';
        option.label.appendChild(suffix);
        return suffix;
      });
  }

  bindPackEvents() {
    const { options, mixToggle, mixRows } = this.packEls;
    if (options) {
      options.addEventListener('change', (event) => {
        if (event.target.classList.contains('wr-pack__input')) {
          this.selectPack(parseInt(event.target.value, 10));
        }
      });
    }
    if (mixToggle) {
      mixToggle.addEventListener('click', () => this.setPackMixOpen(!this.packMixOpen));
    }
    if (mixRows) {
      mixRows.addEventListener('change', (event) => {
        if (event.target.matches('select[data-row]')) this.onPackRowChange(event.target);
      });
    }
    document.addEventListener('variant:change', (event) => {
      this.handlePackVariantChange(event.detail ? event.detail.variant : null);
    });
  }

  selectPack(quantity) {
    if (!this.packTiers.some((tier) => tier.quantity === quantity)) return;
    this.currentBundle.quantity = quantity;
    if (quantity <= 1 && this.packMixOpen) {
      this.setPackMixOpen(false);
      return;
    }
    this.fillPackItems();
    if (this.packMixOpen) this.renderPackMixRows();
    this.renderPackState();
  }

  handlePackVariantChange(variant) {
    if (!variant) return;
    const known = this.packVariantById.get(String(variant.id)) || variant;
    // enhanced-variant-picker dispatches variant:change more than once per
    // selection; skip the repeats.
    if (this.packMainVariant && String(this.packMainVariant.id) === String(known.id)) return;
    this.packMainVariant = known;
    // The picker above sets every shirt, including open per-shirt rows.
    this.currentBundle.items = this.currentBundle.items.map(() => this.makePackItem(known));
    if (this.packMixOpen) this.renderPackMixRows();
    this.renderPackState();
  }

  setPackMixOpen(open) {
    this.packMixOpen = open;
    const { mixToggle, mixLabel, mixIcon, mixRows } = this.packEls;
    if (mixToggle) mixToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (mixLabel) mixLabel.textContent = open ? 'Use the same color & size for all' : 'Mix colors or sizes';
    if (mixIcon) mixIcon.textContent = open ? '−' : '+';
    this.currentBundle.items = [];
    this.fillPackItems();
    if (mixRows) {
      if (open) {
        this.renderPackMixRows();
      } else {
        mixRows.innerHTML = '';
      }
      mixRows.hidden = !open;
    }
    this.renderPackState();
  }

  isPackValueAvailable(item, optionIndex, value) {
    const values = item.values.slice();
    values[optionIndex] = value;
    const exact = this.packVariantByKey.get(values.join('\u0000'));
    if (exact && exact.available) return true;
    // A color stays selectable if any size is in stock; the size then moves.
    const option = this.packOptions.find((candidate) => candidate.index === optionIndex);
    if (!option || !option.isColor) return false;
    return (this.variantsData || []).some((variant) => variant.available && this.getPackVariantValues(variant)[optionIndex] === value);
  }

  renderPackMixRows() {
    const container = this.packEls.mixRows;
    if (!container) return;
    const quantity = this.currentBundle.quantity;
    const firstFreeIndex = quantity - (this.getPackTier(quantity).freeUnits || 0);
    const noun = this.getPackNoun(1, true);
    const sectionId = this.getSectionId();

    container.innerHTML = this.currentBundle.items.map((item, row) => {
      const number = row + 1;
      const fields = this.packOptions.map((option) => {
        const selectId = `WrPackMix-${sectionId}-${row}-${option.index}`;
        return `
          <div class="wr-pack-mix__field${option.isColor ? ' wr-pack-mix__field--color' : ''}">
            <label class="wr-pack-mix__field-label" for="${selectId}">${this.escapePackText(option.name)}</label>
            <select id="${selectId}" class="wr-pack-mix__select" data-row="${row}" data-option-index="${option.index}"
                    aria-label="${this.escapePackText(noun + ' ' + number + ' ' + option.name.toLowerCase())}">
              ${this.renderPackSelectOptions(item, option)}
            </select>
          </div>`;
      }).join('');
      return `
        <div class="wr-pack-mix__row" role="group" aria-label="${this.escapePackText(noun + ' ' + number)}">
          <div class="wr-pack-mix__num">
            <span class="wr-pack-mix__name">${this.escapePackText(noun)} ${number}</span>
            ${row >= firstFreeIndex ? '<span class="wr-pack-mix__free">FREE</span>' : ''}
          </div>
          ${this.renderPackSwatch(item)}
          ${fields}
        </div>`;
    }).join('');
  }

  renderPackSelectOptions(item, option) {
    const current = item.values[option.index];
    return option.values.map((value) => {
      const available = this.isPackValueAvailable(item, option.index, value);
      const safe = this.escapePackText(value);
      return `<option value="${safe}"${value === current ? ' selected' : ''}${available ? '' : ' disabled'}>${safe}${available ? '' : ' (sold out)'}</option>`;
    }).join('');
  }

  renderPackSwatch(item) {
    const colorOption = this.packOptions.find((option) => option.isColor);
    const image = colorOption ? this.getVariantImageForColor(item.values[colorOption.index]) : null;
    return image
      ? `<img class="wr-pack-mix__swatch" src="${this.escapePackText(image)}" alt="" width="26" height="26" loading="lazy">`
      : '<span class="wr-pack-mix__swatch" aria-hidden="true"></span>';
  }

  onPackRowChange(select) {
    const row = parseInt(select.dataset.row, 10);
    const optionIndex = parseInt(select.dataset.optionIndex, 10);
    const item = this.currentBundle.items[row];
    if (!item) return;

    const values = item.values.slice();
    values[optionIndex] = select.value;
    let variant = this.packVariantByKey.get(values.join('\u0000'));
    if (!variant || !variant.available) {
      // Keep the value the shopper picked; move the other options to an in-stock match.
      variant = (this.variantsData || []).find((candidate) => candidate.available &&
        this.getPackVariantValues(candidate)[optionIndex] === select.value) || variant;
    }
    this.currentBundle.items[row] = variant ? this.makePackItem(variant) : { variantId: null, values };

    // Update this row in place so the focused select keeps focus.
    const rowElement = this.packEls.mixRows ? this.packEls.mixRows.children[row] : null;
    if (rowElement) {
      const updated = this.currentBundle.items[row];
      rowElement.querySelectorAll('select[data-row]').forEach((rowSelect) => {
        const option = this.packOptions.find((candidate) => candidate.index === parseInt(rowSelect.dataset.optionIndex, 10));
        if (!option) return;
        Array.from(rowSelect.options).forEach((optionElement) => {
          const available = this.isPackValueAvailable(updated, option.index, optionElement.value);
          optionElement.disabled = !available;
          optionElement.textContent = optionElement.value + (available ? '' : ' (sold out)');
        });
        rowSelect.value = updated.values[option.index];
      });
      const swatch = rowElement.querySelector('.wr-pack-mix__swatch');
      if (swatch) swatch.outerHTML = this.renderPackSwatch(updated);
    }
    this.renderPackState();
  }

  isPackSelectionAvailable() {
    return this.currentBundle.items.every((item) => {
      const variant = this.packVariantById.get(String(item.variantId));
      return !!variant && variant.available !== false;
    });
  }

  /**
   * Disable the add-to-cart button while any pack row is sold out, so the label
   * and the button agree. Only undoes a block this picker set itself; the theme's
   * own sold-out handling for the main variant is left alone.
   */
  setPackAtcBlocked(blocked) {
    const button = this.addToCartBtn;
    if (!button) return;
    if (blocked) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      this.packAtcBlocked = true;
    } else if (this.packAtcBlocked) {
      this.packAtcBlocked = false;
      if (!this.packMainVariant || this.packMainVariant.available !== false) {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      }
    }
  }

  getPackCtaSubText(quantity, totals) {
    if (totals.saveCents > 0) {
      // Free shipping is US-only ($99+), so the line says so.
      const freeShipping = this.qualifiesForPackFreeShipping(totals.totalCents);
      return 'You save ' + this.formatPackMoney(totals.saveCents) + (freeShipping ? ' + free US shipping' : '');
    }
    const next = this.packTiers.find((tier) => tier.quantity > quantity);
    if (quantity === 1 && next && next.quantity === 2 && next.discount > 0 && !next.freeUnits) {
      return `Add a 2nd ${this.getPackNoun(1)} and save ${Math.round(next.discount * 100)}%`;
    }
    return '';
  }

  getPackStickyLine() {
    if (!this.isPack || !this.packTotals) return '';
    const quantity = this.currentBundle.quantity;
    return quantity + ' ' + this.getPackNoun(quantity) + ' · ' + this.formatPackMoney(this.packTotals.totalCents);
  }

  renderPackState() {
    const quantity = this.currentBundle.quantity;
    const unitCents = this.packMainVariant ? this.packMainVariant.price : Math.round(this.currentBundle.basePrice * 100);
    const els = this.packEls;

    els.optionRows.forEach((option) => {
      const selected = option.quantity === quantity;
      const rowTotals = this.getPackTotals(option.quantity, new Array(option.quantity).fill(unitCents));
      option.row.classList.toggle('is-selected', selected);
      option.input.checked = selected;
      // Per-shirt price is display only (total / quantity, rounded to the cent);
      // the total is what the cart charges and is never adjusted to match it.
      option.each.textContent = this.formatPackMoney(Math.round(rowTotals.totalCents / option.quantity));
      option.total.textContent = this.formatPackMoney(rowTotals.totalCents);
      if (option.ship) option.ship.hidden = !this.qualifiesForPackFreeShipping(rowTotals.totalCents);
    });

    const unitPrices = this.currentBundle.items.map((item) => {
      const variant = this.packVariantById.get(String(item.variantId));
      return variant ? variant.price : unitCents;
    });
    const totals = this.getPackTotals(quantity, unitPrices);
    this.packTotals = totals;

    // Keep the legacy contract used by product-form and the sticky bar.
    this.currentBundle.basePrice = unitCents / 100;
    this.currentBundle.discountedPrice = totals.totalCents / quantity / 100;
    this.currentBundle.total = totals.totalCents / 100;
    this.currentBundle.retailTotal = totals.wasCents / 100;

    const packAvailable = this.isPackSelectionAvailable();
    if (els.atcText) {
      if (packAvailable) {
        // The regular price shows struck through before the pack price when the pack saves money.
        const was = totals.saveCents > 0
          ? '<span class="visually-hidden">Regular price</span><s class="wr-pack-atc__was">' +
            this.escapePackText(this.formatPackMoney(totals.wasCents)) + '</s> <span class="visually-hidden">Sale price</span>'
          : '';
        els.atcText.innerHTML = 'Add ' + quantity + ' ' + this.escapePackText(this.getPackNoun(quantity)) +
          ' to cart ' + was + this.escapePackText(this.formatPackMoney(totals.totalCents));
      } else {
        els.atcText.textContent = 'Sold out';
      }
    }
    this.setPackAtcBlocked(!packAvailable);
    if (els.ctaSub) els.ctaSub.textContent = this.getPackCtaSubText(quantity, totals);

    const suffix = quantity > 1 && !this.packMixOpen ? ` (all ${quantity} ${this.getPackNoun(quantity)})` : '';
    els.suffixes.forEach((element) => { element.textContent = suffix; });
    if (els.mix) els.mix.hidden = quantity <= 1;

    document.dispatchEvent(new CustomEvent('bundle:updated', {
      detail: { quantity, total: this.currentBundle.total, retailTotal: this.currentBundle.retailTotal, pack: true }
    }));
  }

  addBundleStyles() {
    if (document.getElementById('bundle-pricing-styles')) return;
      
    const styles = document.createElement('style');
    styles.id = 'bundle-pricing-styles';
    styles.textContent = `
        /*
         * Brand v2.1 (every PDP, Helios pattern). Token vars carry literal
         * fallbacks because standalone Helios layouts load this script without
         * wr-brand-tokens.css. Selected tier = 2px dark-blue edge on white (drawn
         * as 1px border + 1px inset ring so the card never changes size).
         */

        /* Hide quantity selector when bundle pricing is present */
        .bundle-pricing-container ~ * [data-block-type="quantity_selector"],
        .bundle-pricing-container ~ .product-form__input.product-form__quantity {
          display: none !important;
        }

        .bundle-pricing-container {
          margin: 1.5rem 0;
          padding: 0;
          background: transparent;
          border: none;
        }

        .bundle-title {
          font-size: var(--wr-text-base, 16px);
          font-weight: var(--wr-weight-semibold, 600);
          line-height: var(--wr-leading-tight, 1.15);
          margin-bottom: 1rem;
          color: var(--wr-ink, #1a1a1a);
          text-align: left;
          text-transform: none;
          letter-spacing: 0;
        }

        .bundle-subtitle {
          font-weight: var(--wr-weight-regular, 400);
          text-transform: none;
          color: var(--wr-gray, #5c5c5c);
        }

        .bundle-options {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }

        .bundle-option {
          border: 1px solid var(--wr-border, #e2e2e2);
          border-radius: var(--wr-radius-md, 8px);
          padding: 1rem;
          text-align: center;
          cursor: pointer;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
          background: var(--wr-white, #fff);
          color: var(--wr-ink, #1a1a1a);
          position: relative;
          min-height: 80px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .bundle-option:hover {
          border-color: var(--wr-dark-blue, #1362af);
        }

        .bundle-option[data-selected="true"],
        .bundle-option.single-item[data-selected="true"] {
          background: var(--wr-white, #fff);
          color: var(--wr-ink, #1a1a1a);
          border-color: var(--wr-dark-blue, #1362af);
          box-shadow: inset 0 0 0 1px var(--wr-dark-blue, #1362af);
        }

        .bundle-quantity {
          font-size: var(--wr-text-base, 16px);
          font-weight: var(--wr-weight-semibold, 600);
          line-height: var(--wr-leading-tight, 1.15);
          color: inherit;
          margin-bottom: 0.125rem;
          text-transform: none;
          letter-spacing: 0;
        }

        .bundle-price {
          font-size: var(--wr-text-base, 16px);
          color: var(--wr-ink, #1a1a1a);
          font-weight: var(--wr-weight-regular, 400);
          line-height: var(--wr-leading-tight, 1.15);
        }

        .bundle-subtext,
        .bundle-option[data-selected="true"] .bundle-subtext {
          font-size: var(--wr-text-sm, 14px);
          font-weight: var(--wr-weight-semibold, 600);
          line-height: var(--wr-leading-snug, 1.3);
          color: var(--wr-success, #2e7d4f);
          margin-top: 0.25rem;
          text-transform: none;
          letter-spacing: 0;
        }

        /* "Save X%" tier badge: 12px uppercase label (the one size uppercase is allowed). */
        .bundle-discount,
        .bundle-option[data-selected="true"] .bundle-discount {
          position: absolute;
          top: -10px;
          left: 50%;
          transform: translateX(-50%);
          background: var(--wr-dark-blue, #1362af);
          color: var(--wr-white, #fff);
          font-size: var(--wr-text-xs, 12px);
          font-weight: var(--wr-weight-semibold, 600);
          padding: 4px 8px;
          border-radius: var(--wr-radius-sm, 4px);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          line-height: 1;
          box-shadow: none;
          white-space: nowrap;
        }

        .best-offer,
        .bundle-option[data-selected="true"] .best-offer {
          background: var(--wr-dark-blue, #1362af) !important;
          color: var(--wr-white, #fff) !important;
        }

        .bundle-customization {
          margin-top: 1.5rem;
          padding: 1rem;
          background: var(--wr-white, #fff);
          border-radius: var(--wr-radius-md, 8px);
        }

        .bundle-customization-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          cursor: pointer;
        }

        .customization-label {
          font-size: var(--wr-text-base, 16px);
          font-weight: var(--wr-weight-semibold, 600);
          color: var(--wr-ink, #1a1a1a);
        }

        .collapse-btn {
          background: none;
          border: 1px solid var(--wr-border, #e2e2e2);
          border-radius: var(--wr-radius-sm, 4px);
          padding: 4px 8px;
          font-size: var(--wr-text-sm, 14px);
          color: var(--wr-ink, #1a1a1a);
          cursor: pointer;
        }

        .bundle-customization-header:hover .collapse-btn {
          border-color: var(--wr-gray, #5c5c5c);
        }

        .collapse-btn:focus-visible {
          outline: 2px solid var(--wr-dark-blue, #1362af);
          outline-offset: 2px;
        }

        .bundle-item-row {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.5rem 0.75rem 0.375rem;
          border: 1px solid var(--wr-border, #e2e2e2);
          border-radius: var(--wr-radius-md, 8px);
          margin-bottom: 0.375rem;
        }

        .item-number {
          font-weight: var(--wr-weight-semibold, 600);
          min-width: 60px;
          color: var(--wr-gray, #5c5c5c);
          font-size: var(--wr-text-sm, 14px);
        }

        .item-selects {
          display: flex;
          gap: 0.5rem;
          flex: 1;
        }

        .item-select {
          flex: 1;
          padding: 8px 12px;
          border: 1px solid var(--wr-border, #e2e2e2);
          border-radius: var(--wr-radius-sm, 4px);
          font-size: var(--wr-text-base, 16px);
          font-weight: var(--wr-weight-regular, 400);
          color: var(--wr-ink, #1a1a1a);
          background-color: var(--wr-white, #fff);
        }

        .item-select:hover {
          border-color: var(--wr-dark-blue, #1362af);
        }

        .item-select:focus-visible {
          outline: 2px solid var(--wr-dark-blue, #1362af);
          outline-offset: 2px;
          box-shadow: none;
        }

        /* Custom Color Dropdown Styles */
        .color-dropdown-wrapper {
          flex: 1;
          position: relative;
        }

        .color-dropdown-trigger {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 6px 12px;
          border: 1px solid var(--wr-border, #e2e2e2);
          border-radius: var(--wr-radius-sm, 4px);
          background-color: var(--wr-white, #fff);
          color: var(--wr-ink, #1a1a1a);
          cursor: pointer;
          font-size: var(--wr-text-base, 16px);
          font-weight: var(--wr-weight-regular, 400);
          text-align: left;
          transition: border-color 0.2s ease;
        }

        .color-dropdown-trigger:hover {
          border-color: var(--wr-dark-blue, #1362af);
        }

        .color-dropdown-trigger:focus-visible {
          outline: 2px solid var(--wr-dark-blue, #1362af);
          outline-offset: 2px;
          box-shadow: none;
        }

        .color-dropdown-selected {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .color-swatch-img {
          width: 28px;
          height: 28px;
          border-radius: var(--wr-radius-sm, 4px);
          object-fit: cover;
          border: 1px solid var(--wr-border, #e2e2e2);
        }

        .color-swatch-fallback {
          width: 28px;
          height: 28px;
          border-radius: var(--wr-radius-sm, 4px);
          background: var(--wr-off-white, #f8f8f8);
          border: 1px solid var(--wr-border, #e2e2e2);
        }

        .color-dropdown-value {
          color: var(--wr-ink, #1a1a1a);
        }

        .color-dropdown-arrow {
          color: var(--wr-gray, #5c5c5c);
          transition: transform 0.2s ease;
          flex-shrink: 0;
        }

        /* Dropdown menus may carry the brand shadow. */
        .color-dropdown-menu {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          right: 0;
          background: var(--wr-white, #fff);
          border: 1px solid var(--wr-border, #e2e2e2);
          border-radius: var(--wr-radius-sm, 4px);
          box-shadow: var(--wr-shadow, 0 2px 8px rgb(26 26 26 / 8%));
          z-index: 100;
          display: none;
          max-height: 240px;
          overflow-y: auto;
        }

        .color-dropdown-menu.open {
          display: block;
        }

        .color-dropdown-option {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          border: none;
          background: none;
          cursor: pointer;
          font-size: var(--wr-text-base, 16px);
          text-align: left;
          transition: background-color 0.15s ease;
        }

        .color-dropdown-option:hover,
        .color-dropdown-option.selected {
          background-color: var(--wr-off-white, #f8f8f8);
        }

        .color-dropdown-option.selected .color-option-name {
          font-weight: var(--wr-weight-semibold, 600);
        }

        .color-dropdown-option:focus-visible {
          outline: 2px solid var(--wr-dark-blue, #1362af);
          outline-offset: -2px;
        }

        .color-option-name {
          flex: 1;
          color: var(--wr-ink, #1a1a1a);
        }

        .color-check {
          color: var(--wr-dark-blue, #1362af);
          flex-shrink: 0;
        }

        .color-dropdown-option:first-child {
          border-radius: 3px 3px 0 0;
        }

        .color-dropdown-option:last-child {
          border-radius: 0 0 3px 3px;
        }

        .color-dropdown-option:not(:last-child) {
          border-bottom: 1px solid var(--wr-border, #e2e2e2);
        }

        .bundle-summary {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 0;
          border-top: 1px solid var(--wr-border, #e2e2e2);
          margin-top: 1rem;
        }

        .bundle-total {
          font-size: var(--wr-text-xl, 20px);
          font-weight: var(--wr-weight-semibold, 600);
          color: var(--wr-ink, #1a1a1a);
        }

        .bundle-savings {
          color: var(--wr-success, #2e7d4f);
          font-weight: var(--wr-weight-semibold, 600);
        }

        @media (max-width: 767px) {
          .bundle-options {
            grid-template-columns: repeat(3, 1fr);
            gap: 0.25rem;
          }

          .bundle-option {
            padding: 0.5rem 0.25rem;
            min-height: 60px;
          }

          .bundle-discount {
            padding: 4px 6px;
            top: -10px;
            left: 50%;
            transform: translateX(-50%);
          }

          .bundle-customization {
            padding: 0.875rem;
          }

          .bundle-item-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.375rem;
            padding: 0.75rem;
          }

          .item-number {
            min-width: auto;
            margin-bottom: 0.25rem;
          }

          .item-selects {
            flex-direction: column;
            gap: 0.5rem;
            width: 100%;
          }

          .item-select {
            padding: 10px 14px;
          }

          /* Mobile color dropdown styles */
          .color-dropdown-trigger {
            padding: 10px 14px;
          }

          .color-swatch-img,
          .color-swatch-fallback {
            width: 32px;
            height: 32px;
          }

          .color-dropdown-option {
            padding: 12px 14px;
          }

          .color-dropdown-menu {
            max-height: 280px;
          }
        }
      `;

    document.head.appendChild(styles);
  }
}
  
// Initialize bundle pricing
let bundlePricing;
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    bundlePricing = new BundlePricingManager();
  });
} else {
  bundlePricing = new BundlePricingManager();
}
  
// Make it globally available for onclick handlers and re-initialization
window.bundlePricing = bundlePricing;
window.BundlePricingManager = BundlePricingManager;